import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  LockKeyhole,
  RefreshCw,
  Wallet,
  X,
} from 'lucide-react';
import { getCurrentProfile } from '../../services/auth/authService';
import { db } from '../../db/database';
import {
  closeShift,
  getOpenShift,
  getShiftReconciliation,
  listShiftHistory,
  openShift,
  type ShiftActor,
  type ShiftReconciliation,
} from '../../domain/shifts/shiftService';
import type { ShiftRecord } from '../../db/types';
import { PricingEngine } from '../../domain/pricing';

type Props = { onClose: () => void };
const money = (centavos: number) => PricingEngine.formatCentavos(centavos);
const parsePeso = (value: string) => {
  const n = Number(value.replace(/,/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};

export function ShiftManagementDialog({ onClose }: Props) {
  const [actor, setActor] = useState<ShiftActor>({
    id: 'cashier-local',
    role: 'cashier',
    deviceId: 'device-local',
  });
  const [open, setOpen] = useState<ShiftRecord | undefined>();
  const [history, setHistory] = useState<ShiftRecord[]>([]);
  const [opening, setOpening] = useState('');
  const [actual, setActual] = useState('');
  const [recon, setRecon] = useState<ShiftReconciliation>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const refresh = async () => {
    const current = await getOpenShift(actor.id);
    setOpen(current);
    setHistory(await listShiftHistory());
    setRecon(current ? await getShiftReconciliation(current) : undefined);
  };
  useEffect(() => {
    void (async () => {
      try {
        const profile = await getCurrentProfile();
        if (profile)
          setActor({
            id: profile.id,
            role: profile.role,
            deviceId: (await db.settings.get('cloud.deviceId'))?.value ?? 'device-local',
          });
      } catch {
        /* offline/local mode retains safe fallback identity */
      }
    })();
  }, []);
  useEffect(() => {
    void refresh();
  }, [actor.id]);
  const variance = useMemo(() => {
    const actualCentavos = parsePeso(actual);
    return Number.isFinite(actualCentavos) && recon
      ? actualCentavos - recon.expectedCashCentavos
      : undefined;
  }, [actual, recon]);
  const submitOpen = async () => {
    setError('');
    const amount = parsePeso(opening);
    if (!Number.isFinite(amount)) return setError('Enter a valid opening cash amount.');
    setBusy(true);
    try {
      await openShift({ actor, openingCashCentavos: amount });
      setOpening('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to open shift.');
    } finally {
      setBusy(false);
    }
  };
  const submitClose = async () => {
    setError('');
    const amount = parsePeso(actual);
    if (!Number.isFinite(amount)) return setError('Enter a valid actual cash amount.');
    if (!open) return;
    setBusy(true);
    try {
      await closeShift({ shiftId: open.id, actor, actualCashCentavos: amount });
      setActual('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to close shift.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/45 p-4">
      <div className="flex max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-[#F7FAFC] shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#00549C]">
              Cash control
            </p>
            <h2 className="mt-1 text-2xl font-black text-[#003B70]">Shift Management</h2>
            <p className="mt-1 text-sm font-semibold text-slate-500">{actor.role.toUpperCase()}</p>
          </div>
          <button
            onClick={onClose}
            className="grid min-h-12 min-w-12 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500"
          >
            <X />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-5 lg:p-6">
          {error && (
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
              <AlertTriangle size={18} />
              {error}
            </div>
          )}
          {!open ? (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#DCEEFF] text-[#00549C]">
                  <Wallet />
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#003B70]">Open Shift</h3>
                  <p className="mt-1 text-sm font-semibold text-slate-500">
                    Record the cash physically present at the start of the shift.
                  </p>
                </div>
              </div>
              <label className="mt-6 block max-w-md text-sm font-black text-slate-700">
                Opening Cash
                <input
                  inputMode="decimal"
                  value={opening}
                  onChange={(e) => setOpening(e.target.value)}
                  placeholder="0.00"
                  className="mt-2 min-h-16 w-full rounded-xl border border-slate-200 px-5 text-2xl font-black tabular-nums outline-none focus:border-[#00549C]"
                />
              </label>
              <button
                disabled={busy}
                onClick={() => void submitOpen()}
                className="mt-5 min-h-16 w-full max-w-md rounded-xl bg-[#00549C] text-lg font-black text-white disabled:opacity-50"
              >
                {busy ? 'OPENING…' : 'OPEN SHIFT'}
              </button>
            </section>
          ) : (
            <div className="space-y-5">
              <section className="rounded-2xl border border-[#9EC6E9] bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="text-emerald-600" />
                      <h3 className="text-xl font-black text-[#003B70]">Shift Open</h3>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      Opened {new Date(open.openedAt).toLocaleString('en-PH')}
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700">
                    OPEN
                  </span>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <Metric
                    label="Opening Cash"
                    value={money(recon?.openingCashCentavos ?? open.openingCashCentavos)}
                  />
                  <Metric label="Cash Sales" value={money(recon?.cashSalesCentavos ?? 0)} />
                  <Metric label="Cash Refunds" value={money(recon?.cashRefundsCentavos ?? 0)} />
                  <Metric label="Expected Cash" value={money(recon?.expectedCashCentavos ?? 0)} />
                </div>
              </section>
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <LockKeyhole className="mt-1 text-[#00549C]" />
                  <div>
                    <h3 className="text-xl font-black text-[#003B70]">Close Shift</h3>
                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      Count the physical cash and compare it with expected cash.
                    </p>
                  </div>
                </div>
                <label className="mt-5 block max-w-md text-sm font-black text-slate-700">
                  Actual Cash
                  <input
                    inputMode="decimal"
                    value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    placeholder="0.00"
                    className="mt-2 min-h-16 w-full rounded-xl border border-slate-200 px-5 text-2xl font-black tabular-nums outline-none focus:border-[#00549C]"
                  />
                </label>
                {variance !== undefined && <Variance value={variance} />}
                <button
                  disabled={busy}
                  onClick={() => void submitClose()}
                  className="mt-5 min-h-16 w-full max-w-md rounded-xl bg-[#003B70] text-lg font-black text-white disabled:opacity-50"
                >
                  {busy ? 'CLOSING…' : 'CLOSE SHIFT'}
                </button>
              </section>
            </div>
          )}
          <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="font-black text-[#003B70]">Shift History</h3>
                <p className="mt-1 text-xs font-semibold text-slate-400">
                  Completed and current shift records
                </p>
              </div>
              <button
                onClick={() => void refresh()}
                className="grid min-h-10 min-w-10 place-items-center rounded-lg border border-slate-200"
              >
                <RefreshCw size={16} />
              </button>
            </div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="px-5 py-3">Opened</th>
                    <th className="px-5 py-3">Cashier</th>
                    <th className="px-5 py-3">Opening</th>
                    <th className="px-5 py-3">Expected</th>
                    <th className="px-5 py-3">Actual</th>
                    <th className="px-5 py-3">Variance</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-8 text-center font-semibold text-slate-400"
                      >
                        No shift history yet.
                      </td>
                    </tr>
                  ) : (
                    history.map((s) => (
                      <tr key={s.id} className="border-t border-slate-100">
                        <td className="px-5 py-3 font-bold text-slate-700">
                          {new Date(s.openedAt).toLocaleString('en-PH')}
                        </td>
                        <td className="px-5 py-3 font-semibold text-slate-500">{s.cashierId}</td>
                        <td className="px-5 py-3 font-semibold">{money(s.openingCashCentavos)}</td>
                        <td className="px-5 py-3 font-semibold">
                          {money(s.expectedCashCentavos ?? s.openingCashCentavos)}
                        </td>
                        <td className="px-5 py-3 font-semibold">
                          {s.actualCashCentavos == null ? '—' : money(s.actualCashCentavos)}
                        </td>
                        <td className="px-5 py-3 font-black">
                          {s.varianceCentavos == null ? '—' : money(s.varianceCentavos)}
                        </td>
                        <td className="px-5 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black ${s.status === 'OPEN' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}
                          >
                            <Clock3 size={13} />
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-[#F7FAFC] p-4">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-2 text-lg font-black tabular-nums text-[#003B70]">{value}</p>
    </div>
  );
}
function Variance({ value }: { value: number }) {
  const zero = value === 0;
  return (
    <div
      className={`mt-5 flex items-center justify-between rounded-2xl border p-5 ${zero ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
    >
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Variance</p>
        <p
          className={`mt-1 text-3xl font-black tabular-nums ${zero ? 'text-emerald-700' : 'text-amber-700'}`}
        >
          {money(Math.abs(value))}
        </p>
      </div>
      <p className={`text-sm font-black ${zero ? 'text-emerald-700' : 'text-amber-700'}`}>
        {zero ? 'BALANCED' : value > 0 ? 'OVER' : 'SHORT'}
      </p>
    </div>
  );
}
